import repositoryConfig from '../../../../config.json';
import { supabase } from '../supabase/client.js';

const AVATAR_MAX_BYTES = 5 * 1024 * 1024;
const AVATAR_TYPES = new Set(['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif', 'image/avif']);

function requireClient() {
  if (!supabase) throw new Error('WatchVIM account services are temporarily unavailable. Please try again shortly.');
  return supabase;
}

function normalizeEmail(email) {
  return String(email || '').trim().toLowerCase();
}

function publicSiteUrl() {
  const configured = String(
    import.meta.env.VITE_PUBLIC_SITE_URL || repositoryConfig.PUBLIC_SITE_URL || '',
  ).trim().replace(/\/$/, '');
  if (configured) return configured;
  if (typeof window !== 'undefined') return window.location.origin.replace(/\/$/, '');
  return '';
}

function accountError(error, fallback) {
  const message = String(error?.message || '');
  if (/invalid api key|apikey|api key/i.test(message)) {
    return new Error('WatchVIM account services are temporarily unavailable. Please try again shortly.');
  }
  return error || new Error(fallback);
}

function avatarFromUser(user) {
  return String(user?.user_metadata?.avatar_url || user?.user_metadata?.avatarUrl || '').trim();
}

async function syncAvatarToActiveSession(avatarUrl) {
  const client = requireClient();
  const normalizedAvatar = String(avatarUrl || '').trim();
  const { error } = await client.auth.updateUser({
    data: {
      avatar_url: normalizedAvatar,
      avatarUrl: normalizedAvatar,
    },
  });
  if (error) {
    // The server endpoint already persisted the avatar. This best-effort client
    // sync refreshes SessionProvider immediately so shared navigation updates
    // without forcing a sign-out, reload, or token refresh.
    console.warn('[WatchVIM V3] Active avatar session sync failed', error);
  }
}

async function apiError(response, fallback) {
  let payload = null;
  try { payload = await response.json(); } catch (_) { /* non-json failure */ }
  const message = String(payload?.error || fallback || `Request failed (${response.status})`);
  const error = new Error(message);
  error.status = response.status;
  error.payload = payload;
  return error;
}

export async function signIn(email, password) {
  const client = requireClient();
  const { data, error } = await client.auth.signInWithPassword({
    email: normalizeEmail(email),
    password,
  });
  if (error) throw accountError(error, 'Unable to log in.');
  if (!data?.session || !data?.user) {
    throw new Error('Login completed without an active WatchVIM session. Please try again.');
  }
  return data;
}

export async function signUp({ email, password, fullName }) {
  const client = requireClient();
  const siteUrl = publicSiteUrl();
  const { data, error } = await client.auth.signUp({
    email: normalizeEmail(email),
    password,
    options: {
      emailRedirectTo: siteUrl ? `${siteUrl}/verify-email` : undefined,
      data: { full_name: String(fullName || '').trim() },
    },
  });
  if (error) throw accountError(error, 'Unable to create your account.');
  return data;
}

export async function sendPasswordReset(email) {
  const client = requireClient();
  const siteUrl = publicSiteUrl();
  const redirectTo = siteUrl ? `${siteUrl}/reset-password` : undefined;
  const { data, error } = await client.auth.resetPasswordForEmail(normalizeEmail(email), { redirectTo });
  if (error) throw accountError(error, 'Unable to send password reset instructions.');
  return data;
}

export async function completePasswordRecovery(password) {
  const client = requireClient();
  const { data, error } = await client.auth.updateUser({ password });
  if (error) throw accountError(error, 'Unable to update your password.');
  const { error: signOutError } = await client.auth.signOut({ scope: 'local' });
  if (signOutError) console.warn('[WatchVIM V3] Recovery session cleanup failed', signOutError);
  return data;
}

export async function signOut() {
  const client = requireClient();
  const { error } = await client.auth.signOut();
  if (error) throw accountError(error, 'Unable to log out.');
}

export async function loadProfile(userId) {
  const client = requireClient();
  if (!userId) return null;
  const { data, error } = await client
    .from('profiles')
    .select('id,email,full_name,avatar_url,created_at')
    .eq('id', userId)
    .maybeSingle();
  if (error) throw error;

  const profile = data || null;
  if (profile?.avatar_url) return profile;

  // Older WatchVIM avatar uploads were stored in Auth metadata only. Keep those
  // users visually intact while the server now synchronizes both stores.
  const { data: authData, error: authError } = await client.auth.getUser();
  if (authError) return profile;
  const legacyAvatar = avatarFromUser(authData?.user);
  return profile ? { ...profile, avatar_url: legacyAvatar || null } : (
    authData?.user?.id === userId
      ? {
          id: userId,
          email: authData.user.email || null,
          full_name: String(authData.user.user_metadata?.full_name || '').trim() || null,
          avatar_url: legacyAvatar || null,
          created_at: authData.user.created_at || null,
        }
      : null
  );
}

export async function updateProfile(userId, patch) {
  const client = requireClient();
  if (!userId) throw new Error('You must be signed in to edit your profile.');
  const safe = {};
  if (patch.full_name !== undefined) safe.full_name = String(patch.full_name || '').trim();
  if (patch.avatar_url !== undefined) safe.avatar_url = String(patch.avatar_url || '').trim() || null;
  const { data, error } = await client
    .from('profiles')
    .update(safe)
    .eq('id', userId)
    .select('id,email,full_name,avatar_url,created_at')
    .maybeSingle();
  if (error) throw error;
  if (!data) throw new Error('Your profile could not be updated. Please sign in again and retry.');

  const metadata = {};
  if (patch.full_name !== undefined) metadata.full_name = safe.full_name;
  if (patch.avatar_url !== undefined) {
    metadata.avatar_url = safe.avatar_url || '';
    metadata.avatarUrl = safe.avatar_url || '';
  }
  if (Object.keys(metadata).length) {
    const { error: authError } = await client.auth.updateUser({ data: metadata });
    if (authError) console.warn('[WatchVIM V3] Profile Auth metadata sync failed', authError);
  }
  return data;
}

export async function uploadProfileAvatar(session, file) {
  if (!session?.access_token) throw new Error('Please sign in again before updating your profile image.');
  if (!file) throw new Error('Choose an image first.');
  const type = String(file.type || '').toLowerCase();
  if (!AVATAR_TYPES.has(type)) throw new Error('Use a JPG, PNG, WebP, GIF, or AVIF image.');
  if (Number(file.size || 0) > AVATAR_MAX_BYTES) throw new Error('Please use an image under 5MB.');

  const body = new FormData();
  body.append('avatar', file, file.name || 'avatar.jpg');
  const response = await fetch('/api/profile/avatar', {
    method: 'POST',
    headers: { Authorization: `Bearer ${session.access_token}` },
    body,
  });
  if (!response.ok) throw await apiError(response, 'Unable to upload the profile image.');
  const payload = await response.json();
  const avatarUrl = String(payload?.avatar_url || '').trim();
  if (!avatarUrl) throw new Error('The profile image uploaded, but WatchVIM did not receive its URL.');
  await syncAvatarToActiveSession(avatarUrl);
  return avatarUrl;
}

export async function removeProfileAvatar(session) {
  if (!session?.access_token) throw new Error('Please sign in again before removing your profile image.');
  const response = await fetch('/api/profile/avatar', {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${session.access_token}` },
  });
  if (!response.ok) throw await apiError(response, 'Unable to remove the profile image.');
  await syncAvatarToActiveSession('');
  return true;
}

export async function loadMembershipPlans() {
  const client = requireClient();
  const { data, error } = await client
    .from('subscription_plans')
    .select('id,name,paypal_plan_id,price_usd,price,currency,interval,is_active')
    .eq('is_active', true)
    .order('interval', { ascending: true });
  if (error) throw error;
  return Array.isArray(data) ? data : [];
}
